// A compact reference can only be used when the original JSON can be recreated
// byte-for-byte. All other records remain on the existing full-snapshot path.
const variableFields = new Set(['id', 'mealId', 'date', 'amount', 'unit', 'createdAt', 'updatedAt', 'deletedAt', 'syncStatus']);
const variableNumbers = new Set(['amount', 'grams', 'calories', 'protein', 'fat', 'carbs', 'kcal100', 'kcalPortion']);
const variableText = new Set(['sourceText', 'portion']);
const validObject = x => x !== null && typeof x === 'object' && !Array.isArray(x);

// Keep measured values separately: historical per-portion rounding and manual
// values cannot safely be reconstructed from today's nutrition catalog.
function template(value, key, values, depth = 0) {
  if (depth > 60) throw new Error('invalid_compact_meal');
  if (typeof value === 'number' && variableNumbers.has(key)) { values.push(value); return ['v']; }
  if (typeof value === 'string' && variableText.has(key) && /\d/u.test(value)) {
    const parts = value.split(/(\d+(?:[.,]\d+)?)/u);
    for (let i = 1; i < parts.length; i += 2) values.push(parts[i]);
    return ['t', parts.filter((_,i) => i % 2 === 0)];
  }
  if (Array.isArray(value)) return ['a', value.map(item => template(item, '', values, depth + 1))];
  if (validObject(value)) return ['o', Object.keys(value).map(k => [k, template(value[k], k, values, depth + 1)])];
  return ['l', value];
}
function restore(node, values, cursor, depth = 0) {
  if (depth > 60 || !Array.isArray(node) || node.length > 2) throw new Error('invalid_compact_meal');
  if (node[0] === 'v' && node.length === 1) {
    const value = values[cursor.i++];
    if (typeof value !== 'number' || !Number.isFinite(value)) throw new Error('invalid_compact_meal');
    return value;
  }
  if (node[0] === 't' && node.length === 2 && Array.isArray(node[1]) && node[1].length > 1 && node[1].every(x => typeof x === 'string')) {
    return node[1].map((part, i) => {
      if (i === node[1].length - 1) return part;
      const value = values[cursor.i++];
      if (typeof value !== 'string' || !/^\d+(?:[.,]\d+)?$/u.test(value)) throw new Error('invalid_compact_meal');
      return part + value;
    }).join('');
  }
  if (node[0] === 'a' && node.length === 2 && Array.isArray(node[1])) return node[1].map(item => restore(item, values, cursor, depth + 1));
  if (node[0] === 'o' && node.length === 2 && Array.isArray(node[1])) {
    const object = Object.create(null);
    for (const pair of node[1]) {
      if (!Array.isArray(pair) || pair.length !== 2 || typeof pair[0] !== 'string' || Object.hasOwn(object, pair[0])) throw new Error('invalid_compact_meal');
      object[pair[0]] = restore(pair[1], values, cursor, depth + 1);
    }
    return object;
  }
  if (node[0] === 'l' && node.length === 2 && (node[1] === null || ['string','number','boolean'].includes(typeof node[1]))) return node[1];
  throw new Error('invalid_compact_meal');
}

export function splitMealItem(payload) {
  let value;
  try { value = JSON.parse(payload); } catch { return null; }
  if (!validObject(value) || typeof value.id !== 'string' || typeof value.mealId !== 'string'
      || typeof value.amount !== 'number' || !Number.isFinite(value.amount) || value.amount <= 0
      || typeof value.displayName !== 'string' || !validObject(value.parsedFood) || !validObject(value.nutritionRow)) return null;
  const product = Object.create(null), fields = Object.create(null), values = [];
  const order = Object.keys(value);
  for (const key of order) (variableFields.has(key) ? fields : product)[key] = value[key];
  let snapshot;
  try { snapshot = JSON.stringify({ version: 2, order, product: template(product, '', values) }); } catch { return null; }
  const reference = { version: 2, fields, values };
  try { if (joinMealItem(snapshot, reference) !== payload) return null; } catch { return null; }
  return { snapshot, reference };
}

export function joinMealItem(snapshot, reference) {
  let product;
  try { product = JSON.parse(snapshot); } catch { throw new Error('invalid_compact_meal'); }
  if (validObject(product) && product.version === 2 && validObject(reference) && reference.version === 2) {
    const { order } = product, fields = reference.fields, values = reference.values;
    if (!Array.isArray(order) || order.length > 100 || new Set(order).size !== order.length
        || order.some(key => typeof key !== 'string') || !validObject(fields) || !Array.isArray(values) || values.length > 10000
        || typeof fields.id !== 'string' || !fields.id || typeof fields.mealId !== 'string' || !fields.mealId
        || typeof fields.amount !== 'number' || !Number.isFinite(fields.amount) || fields.amount <= 0
        || Object.keys(fields).some(key => !variableFields.has(key))) throw new Error('invalid_compact_meal');
    const cursor = { i: 0 }, restored = restore(product.product, values, cursor);
    if (!validObject(restored) || cursor.i !== values.length || Object.keys(restored).some(key => variableFields.has(key))) throw new Error('invalid_compact_meal');
    const keys = [...Object.keys(restored), ...Object.keys(fields)];
    if (keys.length !== order.length || keys.some(key => !order.includes(key))) throw new Error('invalid_compact_meal');
    const result = Object.create(null);
    for (const key of order) result[key] = Object.hasOwn(fields, key) ? fields[key] : restored[key];
    return JSON.stringify(result);
  }
  if (!validObject(product) || !validObject(reference) || reference.version !== 1
      || !validObject(reference.fields) || !Array.isArray(reference.order)
      || reference.order.length > 100 || new Set(reference.order).size !== reference.order.length
      || reference.order.some(key => typeof key !== 'string')) throw new Error('invalid_compact_meal');
  const fields = reference.fields;
  if (typeof fields.id !== 'string' || !fields.id || typeof fields.mealId !== 'string' || !fields.mealId
      || typeof fields.amount !== 'number' || !Number.isFinite(fields.amount) || fields.amount <= 0) throw new Error('invalid_compact_meal');
  const keys = [...Object.keys(product), ...Object.keys(fields)];
  if (keys.length !== reference.order.length || keys.some(key => !reference.order.includes(key))
      || Object.keys(fields).some(key => !variableFields.has(key))
      || Object.keys(product).some(key => variableFields.has(key))) throw new Error('invalid_compact_meal');
  const result = Object.create(null);
  for (const key of reference.order) result[key] = Object.hasOwn(fields, key) ? fields[key] : product[key];
  return JSON.stringify(result);
}
